import { CustomFieldResponseType } from '@business/entities/integration';
import { ExtendedFile } from '@business/entities/common';
import { UserId } from '@business/entities/account';
import { CategoryId, CategoryResponse } from '@business/entities/category';
import { CalendarTemplateId } from '../calendar_template';

export enum CalendarObjectType {
  deal = 'deal',
  project = 'project',
  workOrder = 'workOrder',
  tmInvoiceSchedule = 'tmInvoiceSchedule',
}

export interface CalendarEventObject {
  id: string;
  type: CalendarObjectType;
  isProviderEvent?: boolean;
  otherId?: string;
  otherType?: CalendarObjectType;
}
export interface CalendarEventRequest {
  start: number;
  end: number;
  object?: CalendarEventObject;
}

/** Dates-only update sent by calendar drag & drop / resize (`calendar-events/move/{id}`). */
export interface MoveCalendarEventRequest {
  start: number;
  end: number;
  /** Cascade the day shift to the imported event's dependents (children, grandchildren…) */
  updateAll?: boolean;
}

export interface CalendarEventScheduleRequest {
  templateId: CalendarTemplateId;
}

export type CalendarEventId = string;
export enum CalendarEventType {
  'event' = 'event',
  'outOfOffice' = 'outOfOffice',
  'focusTime' = 'focusTime',
}

export enum CalendarEventUserStatus {
  pending = 'pending',
  accepted = 'accepted',
  declined = 'declined',
}

export interface CalendarEventResponse {
  id: CalendarEventId;
  type: CalendarEventType;
  /**
   * What the calendar shows. On calendars that mix objects (Management system calendar, user
   * calendar) the backend composes "<work order> · <client> · <event>"; elsewhere it is the stored
   * title. Edit forms must use `rawTitle` so the composed value is never saved back.
   */
  title: string;
  /** The stored event title, never decorated with work-order/client context. */
  rawTitle?: string;
  timezone?: string;
  description?: string;
  location?: string;
  notes?: string;

  start: number;
  end: number;

  allDay: boolean;
  allowViewAccess: boolean;
  enableMediaNotification: boolean;

  createdBy: string;
  color?: string;

  reminder?: any;
  reminderDuration?: number;
  reminderIteration?: string;

  group: string;

  users: CalendarEventUserResponse[];
  customFields?: { [key: string]: CustomFieldResponseType };
  files?: ExtendedFile[];
  categoryId?: CategoryId;
  taskAssignments?: CalendarTaskAssignment;
  identifier?: CalendarEventIdentifier;
  /** Set when the event came from a schedule template import (business-day span rendering) */
  importKey?: string | null;
}

export enum CalendarTaskAssignment {
  client = 'client',
  office = 'office',
  pm = 'pm',
  subContractor = 'subContractor',
  salesPerson = 'salesPerson',
}

export enum CalendarEventIdentifier {
  folderPrinted = 'folder_printed',
  permitProcessed = 'permit_processed',
  invoice1Sent = 'invoice_1_sent',
  invoice1Received = 'invoice_1_received',
  invoice2Sent = 'invoice_2_sent',
  invoice2Received = 'invoice_2_received',
  invoice3Sent = 'invoice_3_sent',
  invoice3Received = 'invoice_3_received',
  payReferralFee = 'pay_referral_fee',
  surveySent = 'survey_sent',
}

export const calendarTaskAssignmentTypes = [
  CalendarTaskAssignment.client,
  CalendarTaskAssignment.office,
  CalendarTaskAssignment.pm,
  CalendarTaskAssignment.subContractor,
  CalendarTaskAssignment.salesPerson,
];

export const calendarEventIdentifierTypes = [
  CalendarEventIdentifier.folderPrinted,
  CalendarEventIdentifier.permitProcessed,
  CalendarEventIdentifier.invoice1Sent,
  CalendarEventIdentifier.invoice1Received,
  CalendarEventIdentifier.invoice2Sent,
  CalendarEventIdentifier.invoice2Received,
  CalendarEventIdentifier.invoice3Sent,
  CalendarEventIdentifier.invoice3Received,
  CalendarEventIdentifier.payReferralFee,
  CalendarEventIdentifier.surveySent,
];

export interface CalendarEventScheduleResponse extends CalendarEventResponse {
  category: CategoryResponse;
}

export interface CreateCalendarEventResponse extends Omit<CalendarEventResponse, 'id' | 'createdBy' | 'users'> {
  users: string[];
}

export interface CalendarEventUserResponse {
  id: UserId;
  email: string;
  name: string;
  status: CalendarEventUserStatus;
  optional: boolean;
  eventUserId?: CalendarEventUserId;
  isChecked?: boolean;
  isApproved?: UserId;
}

export type CalendarEventUserId = string;
